/**
 * HIERARQUIA OPERACIONAL: OS → PROJETO → UNIDADE/ÁREA → SUB-OS.
 *
 * A Sub-OS é o seletor operacional do RDO. Escolhido o colaborador e a Sub-OS,
 * tudo o mais — OS, Projeto, Cliente, Unidade/Área — é RESOLVIDO pelos
 * relacionamentos que já existem no cadastro, nunca redigitado:
 *
 *   SubWorkOrder.workOrderId → WorkOrder
 *   WorkOrder.projectId      → Project        (a OS é o pai do Projeto)
 *   Project.clientId         → Client
 *   Project.id               → ProjectUnitArea[]
 *   SubWorkOrder.unitAreaId  → a Unidade, quando a Sub-OS a define
 *
 * Nada aqui infere vínculo por nome ou texto: sem relacionamento, o campo
 * simplesmente fica em aberto.
 */

import { AppError, ErrorCode } from "../../shared/errors/app-error.js";

export interface WorkOrderProjectLinkLike {
  workOrderId: string;
  projectId: string;
}

export interface SubOsLike {
  id: string;
  code: string;
  clientId: string;
  workOrderId: string;
  /** Projeto da Sub-OS dentro da OS. */
  projectId?: string | null;
  /** Responsável principal da Sub-OS, quando o cadastro o define. */
  professionalId?: string | null;
  /** Unidade/Área específica, quando a Sub-OS a determina inequivocamente. */
  unitAreaId?: string | null;
  description?: string | null;
  status?: string | null;
}

export interface WorkOrderLike {
  id: string;
  code: string;
  clientId: string;
  projectId: string;
}

export interface ProjectLike {
  id: string;
  code: string;
  name: string;
  clientId: string;
}

export interface UnitAreaLike {
  id: string;
  code: string;
  name: string;
  projectId: string;
}

export interface SubOsAccessLike {
  professionalId: string;
  subWorkOrderId: string;
  status?: string | null;
}

/** Contexto completo resolvido a partir de UMA Sub-OS. */
export interface SubOsContext {
  subOsId: string;
  subOsCode: string;
  workOrderId: string | null;
  workOrderCode: string;
  projectId: string | null;
  projectLabel: string;
  clientId: string | null;
  clientName: string;
  /** Preenchida só quando o vínculo é inequívoco (ver resolveUnitArea). */
  unitAreaId: string | null;
  unitAreaLabel: string;
  /** Unidades do Projeto entre as quais escolher, quando a Sub-OS não define uma. */
  unitAreaOptions: UnitAreaLike[];
}

export const EMPTY_CONTEXT_FIELD = "—";

export const SUB_OS_REQUIRED_MESSAGE = "Selecione a Sub-OS do apontamento.";
export const SUB_OS_NOT_LINKED_MESSAGE = "A Sub-OS selecionada não está vinculada ao colaborador informado.";
export const NO_SUB_OS_FOR_PROFESSIONAL_MESSAGE = "Nenhuma Sub-OS vinculada a este colaborador.";

const label = (value: string | null | undefined, fallback = EMPTY_CONTEXT_FIELD): string => {
  const raw = String(value ?? "").trim();
  return raw || fallback;
};

/**
 * Sub-OS de um colaborador.
 *
 * Duas formas reais de vínculo, ambas do cadastro existente: o acesso
 * explícito (ProfessionalSubOsAccess ATIVO) e a responsabilidade principal
 * gravada na própria Sub-OS. Nenhum vínculo é inventado.
 */
export function subOsForProfessional(
  subOrders: readonly SubOsLike[],
  accesses: readonly SubOsAccessLike[],
  professionalId: string
): SubOsLike[] {
  if (!professionalId) return [];

  const granted = new Set(
    accesses
      .filter(a => a.professionalId === professionalId && String(a.status ?? "ACTIVE").toUpperCase() === "ACTIVE")
      .map(a => a.subWorkOrderId)
  );

  return subOrders
    .filter(sub => String(sub.status ?? "OPEN").toUpperCase() !== "CANCELLED")
    .filter(sub => granted.has(sub.id) || (sub.professionalId ? sub.professionalId === professionalId : false))
    .sort((a, b) => a.code.localeCompare(b.code, "pt-BR", { numeric: true }));
}

/** A Sub-OS informada pertence mesmo ao colaborador informado? */
export function isSubOsLinkedToProfessional(
  subOrders: readonly SubOsLike[],
  accesses: readonly SubOsAccessLike[],
  professionalId: string,
  subOsId: string
): boolean {
  return subOsForProfessional(subOrders, accesses, professionalId).some(sub => sub.id === subOsId);
}

/**
 * Nunca confiar apenas no que a tela enviou: o serviço revalida o vínculo
 * antes de gravar.
 */
export function assertSubOsLinkedToProfessional(
  subOrders: readonly SubOsLike[],
  accesses: readonly SubOsAccessLike[],
  professionalId: string,
  subOsId: string
): void {
  if (!subOsId) {
    throw new AppError(ErrorCode.Validation, "Sub work order is required", { safeMessage: SUB_OS_REQUIRED_MESSAGE });
  }
  if (!isSubOsLinkedToProfessional(subOrders, accesses, professionalId, subOsId)) {
    throw new AppError(ErrorCode.Authorization, `Sub-OS ${subOsId} not linked to professional ${professionalId}`, {
      safeMessage: SUB_OS_NOT_LINKED_MESSAGE
    });
  }
}

/**
 * Unidade/Área do apontamento.
 *
 * Só é preenchida quando o relacionamento é inequívoco: ou a Sub-OS aponta a
 * unidade, ou o Projeto tem exatamente uma. Com várias unidades e nenhuma
 * indicação na Sub-OS, devolve null — e a escolha fica limitada às unidades
 * daquele Projeto, em vez de o sistema inventar uma.
 */
export function resolveUnitArea(
  subOs: SubOsLike | null,
  projectUnitAreas: readonly UnitAreaLike[]
): { unitArea: UnitAreaLike | null; options: UnitAreaLike[] } {
  const options = [...projectUnitAreas].sort((a, b) => a.code.localeCompare(b.code, "pt-BR", { numeric: true }));

  if (subOs?.unitAreaId) {
    const declared = options.find(area => area.id === subOs.unitAreaId);
    if (declared) return { unitArea: declared, options };
  }

  if (options.length === 1) return { unitArea: options[0]!, options };

  return { unitArea: null, options };
}

export function formatProjectLabel(project: ProjectLike | null): string {
  if (!project) return EMPTY_CONTEXT_FIELD;
  return project.code ? `${project.code} — ${project.name}` : project.name;
}

export function formatUnitAreaLabel(unitArea: UnitAreaLike | null): string {
  if (!unitArea) return EMPTY_CONTEXT_FIELD;
  return unitArea.code ? `${unitArea.code} — ${unitArea.name}` : unitArea.name;
}

/**
 * Resolve a cadeia inteira a partir da Sub-OS. Recebe as entidades já
 * carregadas para poder ser exercitada sem repositório.
 */
export function resolveSubOsContext(input: {
  subOs: SubOsLike | null;
  workOrders: readonly WorkOrderLike[];
  projects: readonly ProjectLike[];
  unitAreas: readonly UnitAreaLike[];
  links?: readonly WorkOrderProjectLinkLike[];
  clientNames?: ReadonlyMap<string, string>;
}): SubOsContext {
  const { subOs, workOrders, projects, unitAreas, clientNames } = input;

  if (!subOs) {
    return {
      subOsId: "", subOsCode: EMPTY_CONTEXT_FIELD,
      workOrderId: null, workOrderCode: EMPTY_CONTEXT_FIELD,
      projectId: null, projectLabel: EMPTY_CONTEXT_FIELD,
      clientId: null, clientName: EMPTY_CONTEXT_FIELD,
      unitAreaId: null, unitAreaLabel: EMPTY_CONTEXT_FIELD,
      unitAreaOptions: []
    };
  }

  const workOrder = workOrders.find(wo => wo.id === subOs.workOrderId) ?? null;

  // O Projeto é o DA SUB-OS. Registros antigos, sem projectId gravado, caem
  // para o Projeto da OS — e só quando a OS tem exatamente um, porque com
  // vários não há como saber qual era.
  const linkedProjects = projectsForWorkOrder(workOrder, projects, input.links ?? []);
  const project = subOs.projectId
    ? projects.find(p => p.id === subOs.projectId) ?? null
    : (linkedProjects.length === 1 ? linkedProjects[0]! : null);
  const projectAreas = project ? unitAreas.filter(area => area.projectId === project.id) : [];
  const { unitArea, options } = resolveUnitArea(subOs, projectAreas);

  // O Cliente vem do Projeto; sem Projeto resolvido, o da própria Sub-OS.
  const clientId = project?.clientId ?? workOrder?.clientId ?? subOs.clientId ?? null;

  return {
    subOsId: subOs.id,
    subOsCode: label(subOs.code),
    workOrderId: workOrder?.id ?? null,
    workOrderCode: label(workOrder?.code),
    projectId: project?.id ?? null,
    projectLabel: formatProjectLabel(project),
    clientId,
    clientName: label(clientId ? clientNames?.get(clientId) : null),
    unitAreaId: unitArea?.id ?? null,
    unitAreaLabel: formatUnitAreaLabel(unitArea),
    unitAreaOptions: options
  };
}

/**
 * Projetos de uma OS — a OS é o pai da estrutura e possui VÁRIOS Projetos.
 *
 * A lista vem dos vínculos formais. O `workOrder.projectId` legado ainda é
 * considerado para instalações cujos vínculos ainda não foram migrados, sem
 * duplicar o Projeto quando ele já está vinculado.
 */
export function projectsForWorkOrder(
  workOrder: WorkOrderLike | null,
  projects: readonly ProjectLike[],
  links: readonly WorkOrderProjectLinkLike[] = []
): ProjectLike[] {
  if (!workOrder) return [];

  const linkedIds = new Set(links.filter(l => l.workOrderId === workOrder.id).map(l => l.projectId));
  if (workOrder.projectId) linkedIds.add(workOrder.projectId);

  return projects
    .filter(project => linkedIds.has(project.id))
    .sort((a, b) => `${a.code} ${a.name}`.localeCompare(`${b.code} ${b.name}`, "pt-BR", { numeric: true }));
}

/** Um Projeto está vinculado a esta OS? */
export function isProjectLinkedToWorkOrder(
  workOrder: WorkOrderLike | null,
  projectId: string,
  links: readonly WorkOrderProjectLinkLike[] = []
): boolean {
  if (!workOrder || !projectId) return false;
  if (workOrder.projectId === projectId) return true;
  return links.some(l => l.workOrderId === workOrder.id && l.projectId === projectId);
}

export const PROJECT_NOT_LINKED_MESSAGE =
  "O Projeto selecionado não está vinculado à Ordem de Serviço informada.";

/** Sub-OS de uma OS, opcionalmente restritas a um Projeto dela. */
export function subOsForWorkOrder(
  subOrders: readonly SubOsLike[],
  workOrderId: string,
  projectId?: string
): SubOsLike[] {
  return subOrders
    .filter(sub => sub.workOrderId === workOrderId)
    .filter(sub => !projectId || sub.projectId === projectId)
    .sort((a, b) => a.code.localeCompare(b.code, "pt-BR", { numeric: true }));
}

/** Sub-OS de um Projeto, em qualquer OS. */
export function subOsForProject(subOrders: readonly SubOsLike[], projectId: string): SubOsLike[] {
  return subOrders
    .filter(sub => sub.projectId === projectId)
    .sort((a, b) => a.code.localeCompare(b.code, "pt-BR", { numeric: true }));
}

/** Um nó da árvore OS → Projetos → Sub-OS da tela de Gestão. */
export interface WorkOrderTreeNode {
  workOrderId: string;
  workOrderCode: string;
  projects: {
    projectId: string;
    projectLabel: string;
    subOrders: { id: string; code: string; description: string | null }[];
  }[];
  /** Sub-OS da OS que ainda não apontam um Projeto (registros antigos). */
  unassignedSubOrders: { id: string; code: string; description: string | null }[];
}

/** Monta a árvore OS → Projetos → Sub-OS. */
export function buildWorkOrderTree(input: {
  workOrders: readonly WorkOrderLike[];
  projects: readonly ProjectLike[];
  links: readonly WorkOrderProjectLinkLike[];
  subOrders: readonly SubOsLike[];
}): WorkOrderTreeNode[] {
  const { workOrders, projects, links, subOrders } = input;

  return [...workOrders]
    .sort((a, b) => a.code.localeCompare(b.code, "pt-BR", { numeric: true }))
    .map(workOrder => {
      const linked = projectsForWorkOrder(workOrder, projects, links);
      const ofWorkOrder = subOrders.filter(sub => sub.workOrderId === workOrder.id);

      return {
        workOrderId: workOrder.id,
        workOrderCode: workOrder.code,
        projects: linked.map(project => ({
          projectId: project.id,
          projectLabel: formatProjectLabel(project),
          subOrders: ofWorkOrder
            .filter(sub => sub.projectId === project.id)
            .sort((a, b) => a.code.localeCompare(b.code, "pt-BR", { numeric: true }))
            .map(sub => ({ id: sub.id, code: sub.code, description: sub.description ?? null }))
        })),
        unassignedSubOrders: ofWorkOrder
          .filter(sub => !sub.projectId)
          .sort((a, b) => a.code.localeCompare(b.code, "pt-BR", { numeric: true }))
          .map(sub => ({ id: sub.id, code: sub.code, description: sub.description ?? null }))
      };
    });
}

/** Trilha OS > Projeto > Sub-OS. */
export function formatSubOsBreadcrumb(context: SubOsContext): string {
  return [context.workOrderCode, context.projectLabel, context.subOsCode]
    .filter(part => part && part !== EMPTY_CONTEXT_FIELD)
    .join(" > ");
}
